import { useState } from "react";
import { Info, EyeOff, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { toast } from "sonner";

export function ExternalJobBadge() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const trpc = useTRPC();

  const { data: jobPreferences } = useQuery(
    trpc.profile.getJobPreferences.queryOptions({}),
  );

  const includeExternalJobs = jobPreferences?.includeExternalJobs ?? true;

  const { mutateAsync: setIncludeExternalJobs, isPending } = useMutation(
    trpc.profile.setIncludeExternalJobs.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["jobPost.search"],
        });
        queryClient.invalidateQueries({
          queryKey: trpc.profile.getJobPreferences.queryKey({}),
        });
      },
    }),
  );

  const handleToggleExternalJobs = async () => {
    const newValue = !includeExternalJobs;
    await setIncludeExternalJobs({ includeExternalJobs: newValue });
    setShowConfirmDialog(false);
    toast.success(
      newValue
        ? "External jobs will now appear in your recommendations"
        : "External jobs hidden from your recommendations",
    );
  };

  return (
    <>
      <Popover>
        <PopoverTrigger>
          <Badge
            variant="outline"
            className="bg-amber-50 border border-amber-200 text-amber-700 px-2.5 py-1 rounded-md text-sm font-medium cursor-pointer hover:bg-amber-100"
          >
            <span>External</span>
            <Info
              className="h-3.5 w-3.5 ml-1.5"
              aria-label="External job info"
            />
          </Badge>
        </PopoverTrigger>
        <PopoverContent className="w-72 sm:w-80 p-4">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">External Job</h4>
            <p className="text-sm text-gray-700">
              This job requires filling out an application on an external site.
              Like this job to get the application link.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-gray-600 hover:text-gray-900"
              onClick={() => setShowConfirmDialog(true)}
            >
              {includeExternalJobs ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Never recommend external jobs
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Show external jobs again
                </>
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {includeExternalJobs
                ? "Hide External Jobs?"
                : "Show External Jobs?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {includeExternalJobs
                ? "This will hide external jobs from your recommended jobs. You can still find them using the job search filters, and you can re-enable them anytime from your profile settings."
                : "This will show external jobs in your recommended jobs again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleExternalJobs}
              disabled={isPending}
            >
              {isPending
                ? includeExternalJobs
                  ? "Hiding..."
                  : "Showing..."
                : includeExternalJobs
                  ? "Hide External Jobs"
                  : "Show External Jobs"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
